import { useEffect, useRef } from 'react';
import type { UserData } from 'types/user';
import {
    createImpersonationBroadcastChannel,
    getImpersonationTabId,
    isImpersonationBroadcastMessage,
} from 'utils/impersonationBroadcast';
import { logError } from 'utils/monitoring';
import { isBroadcastChannelSupported } from 'utils/timecardBroadcast';

type ImpersonationSyncTarget = {
    isLoggedIn: boolean;
    user: Pick<UserData, 'id' | 'authUserId'> | null;
    applySessionUser: (userData: UserData) => void;
};

// Re-fetch the session user instead of trusting a broadcast payload: the shared cookie
// is authoritative, so racing switches from multiple tabs converge on its actual state.
async function syncUserFromSession(
    getTarget: () => ImpersonationSyncTarget,
    isStillLatest: () => boolean
): Promise<void> {
    if (!getTarget().isLoggedIn) return;
    try {
        const response = await fetch(import.meta.env.VITE_API_URL + '/v1/users/current', {
            credentials: 'include',
        });
        // A lapsed session surfaces through the existing 401 handling on the tab's next API call
        if (!response.ok) return;
        const sessionUser = (await response.json()) as UserData;
        // A slow fetch for an earlier signal must not overwrite a newer identity
        if (!isStillLatest()) return;
        const { user, applySessionUser } = getTarget();
        if (user && sessionUser.id === user.id && sessionUser.authUserId === user.authUserId) return;
        applySessionUser(sessionUser);
    } catch (error) {
        logError(error);
    }
}

export function useImpersonationSync(target: ImpersonationSyncTarget): void {
    // Latest target for the mount-once listener (a stable subscription can't close over current state)
    const targetRef = useRef(target);
    useEffect(() => {
        targetRef.current = target;
    });

    useEffect(() => {
        if (!isBroadcastChannelSupported()) return;
        let latestRequestId = 0;
        const channel = createImpersonationBroadcastChannel();
        const handleMessage = (event: MessageEvent) => {
            if (!isImpersonationBroadcastMessage(event.data)) return;
            // Not just belt-and-braces: the sender posts via a separate channel instance,
            // and same-tab delivery between instances does happen.
            if (event.data.payload.senderId === getImpersonationTabId()) return;
            const requestId = ++latestRequestId;
            void syncUserFromSession(
                () => targetRef.current,
                () => requestId === latestRequestId
            );
        };
        channel.addEventListener('message', handleMessage);
        return () => {
            channel.removeEventListener('message', handleMessage);
            channel.close();
        };
    }, []);
}
